import React, { useState } from "react";
import { api, SERVICES } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2, Camera, X } from "lucide-react";

const MIN_DESC = 20;
const MAX_PHOTOS = 6;

const emptyForm = {
  service: "",
  zip_code: "",
  name: "",
  phone: "",
  email: "",
  address: "",
  preferred_date: "",
  description: "",
  item_location: "",
  stairs_or_elevator: null,
  heavy_items: null,
  construction_debris: null,
  same_day_needed: null,
  consent_contact: false,
};

const YesNo = ({ label, value, onChange, testKey }) => (
  <div>
    <p className="mb-2 text-sm font-semibold">{label}</p>
    <div className="flex gap-2">
      {[
        { v: true, label: "Yes" },
        { v: false, label: "No" },
      ].map((opt) => (
        <button
          key={opt.label}
          type="button"
          onClick={() => onChange(opt.v)}
          className={`flex-1 rounded-sm border px-3 py-2 text-sm font-medium transition-colors ${
            value === opt.v
              ? "border-primary bg-primary/10 text-primary"
              : "border-border bg-white hover:border-primary/60"
          }`}
          data-testid={`${testKey}-${opt.label.toLowerCase()}`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  </div>
);

const QuoteForm = ({ variant = "card", defaultService = "" }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ ...emptyForm, service: defaultService });
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const canNext = form.service && /^\d{3,}$/.test(form.zip_code.trim());
  const descLen = form.description.trim().length;
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const canSubmit =
    form.name.trim().length >= 2 &&
    form.phone.trim().length >= 7 &&
    emailValid &&
    form.address.trim().length >= 8 &&
    descLen >= MIN_DESC &&
    form.consent_contact === true;

  const addPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = MAX_PHOTOS - photos.length;
    const accepted = files.slice(0, remaining).map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setPhotos((prev) => [...prev, ...accepted]);
    e.target.value = "";
  };
  const removePhoto = (idx) => {
    setPhotos((prev) => {
      URL.revokeObjectURL(prev[idx].preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      let photo_urls = [];
      if (photos.length > 0) {
        const fd = new FormData();
        photos.forEach(({ file }) => fd.append("files", file));
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        photo_urls = data.urls;
      }

      const payload = { source: window.location.pathname, photo_urls };
      Object.entries(form).forEach(([k, v]) => {
        if (typeof v === "string") {
          const t = v.trim();
          if (t) payload[k] = t;
        } else if (v != null) {
          payload[k] = v;
        }
      });
      await api.post("/leads", payload);
      photos.forEach((p) => URL.revokeObjectURL(p.preview));
      setDone(true);
      toast.success("Request received! A local hauler will reach out shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d.msg).join(" · ")
          : "Something went wrong. Please call us or try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const containerCls =
    variant === "card"
      ? "rounded-sm border border-border bg-card p-6 shadow-sm sm:p-8"
      : "rounded-sm border border-white/15 bg-white/95 p-6 text-foreground shadow-xl sm:p-8";

  if (done) {
    return (
      <div className={containerCls} data-testid="quote-form-success">
        <div className="flex flex-col items-center py-6 text-center">
          <CheckCircle2 className="h-12 w-12 text-[hsl(145,27%,34%)]" />
          <h3 className="mt-4 text-2xl font-black">Request received</h3>
          <p className="mt-2 text-muted-foreground">
            We&apos;ll review your details and photos and call you back with a firm price shortly.
          </p>
          <button
            className="btn-cta mt-6"
            onClick={() => {
              setForm({ ...emptyForm });
              setPhotos([]);
              setDone(false);
              setStep(1);
            }}
            data-testid="quote-form-reset"
          >
            Send another request
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className={containerCls} onSubmit={submit} data-testid="quote-form">
      <div className="flex items-center justify-between">
        <p className="kicker">Free Quote · Step {step} of 3</p>
        <div className="flex gap-1">
          <span className={`h-1.5 w-8 rounded-full ${step >= 1 ? "bg-primary" : "bg-border"}`} />
          <span className={`h-1.5 w-8 rounded-full ${step >= 2 ? "bg-primary" : "bg-border"}`} />
          <span className={`h-1.5 w-8 rounded-full ${step >= 3 ? "bg-primary" : "bg-border"}`} />
        </div>
      </div>
      <h3 className="mt-3 font-display text-2xl font-black sm:text-3xl">
        {step === 1 ? "What do you need hauled?" : step === 2 ? "Job details" : "Contact & confirm"}
      </h3>

      {step === 1 && (
        <div className="mt-6 space-y-5">
          <div>
            <label className="mb-2 block text-sm font-semibold">Service</label>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {SERVICES.map((s) => (
                <label
                  key={s.slug}
                  className={`flex cursor-pointer items-center gap-3 rounded-sm border p-3 text-sm transition-all ${
                    form.service === s.slug ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border hover:border-primary/60"
                  }`}
                  data-testid={`service-option-${s.slug}`}
                >
                  <input type="radio" name="service" value={s.slug} checked={form.service === s.slug} onChange={update("service")} className="accent-[hsl(16,55%,49%)]" />
                  <span className="font-medium">{s.title}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="mb-2 block text-sm font-semibold">ZIP code</label>
            <input
              type="text"
              inputMode="numeric"
              value={form.zip_code}
              onChange={update("zip_code")}
              placeholder="e.g. 33544"
              className="w-full rounded-sm border border-input bg-white px-4 py-3 text-base outline-none focus:border-primary focus:ring-2 focus:ring-primary/40"
              data-testid="quote-zip-input"
            />
          </div>
          <button type="button" disabled={!canNext} onClick={() => setStep(2)} className="btn-cta w-full disabled:cursor-not-allowed disabled:opacity-50" data-testid="quote-next-btn">
            Continue <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="mt-6 space-y-5">
          <div>
            <label className="mb-2 block text-sm font-semibold">Where are the items?</label>
            <select
              value={form.item_location}
              onChange={update("item_location")}
              className="w-full rounded-sm border border-input bg-white px-4 py-3 text-base outline-none focus:border-primary focus:ring-2 focus:ring-primary/40"
              data-testid="quote-location-select"
            >
              <option value="">Select location…</option>
              <option value="inside">Inside the home / building</option>
              <option value="outside">Outside (yard, garage, driveway)</option>
              <option value="curbside">Already at curbside</option>
            </select>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <YesNo label="Stairs or elevator involved?" value={form.stairs_or_elevator} onChange={setField("stairs_or_elevator")} testKey="quote-stairs" />
            <YesNo label="Any heavy items? (safe, piano, hot tub)" value={form.heavy_items} onChange={setField("heavy_items")} testKey="quote-heavy" />
            <YesNo label="Construction / remodel debris?" value={form.construction_debris} onChange={setField("construction_debris")} testKey="quote-construction" />
            <YesNo label="Need same-day pickup?" value={form.same_day_needed} onChange={setField("same_day_needed")} testKey="quote-sameday" />
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold">Desired pickup date (optional)</label>
            <input
              type="date"
              value={form.preferred_date}
              onChange={update("preferred_date")}
              className="w-full rounded-sm border border-input bg-white px-4 py-3 text-base outline-none focus:border-primary focus:ring-2 focus:ring-primary/40"
              data-testid="quote-date-input"
            />
          </div>

          <div className="flex flex-col gap-3 sm:flex-row">
            <button type="button" onClick={() => setStep(1)} className="rounded-sm border border-border bg-white px-6 py-3 text-sm font-semibold hover:bg-muted" data-testid="quote-back-btn">Back</button>
            <button type="button" onClick={() => setStep(3)} className="btn-cta flex-1" data-testid="quote-next2-btn">
              Continue <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="mt-6 space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-semibold">
                Full name <span className="text-primary">*</span>
              </label>
              <input type="text" value={form.name} onChange={update("name")} required className="w-full rounded-sm border border-input bg-white px-4 py-3 outline-none focus:border-primary focus:ring-2 focus:ring-primary/40" data-testid="quote-name-input" />
            </div>
            <div>
              <label className="mb-2 block text-sm font-semibold">
                Phone <span className="text-primary">*</span>
              </label>
              <input type="tel" value={form.phone} onChange={update("phone")} required placeholder="(813) 555-0100" className="w-full rounded-sm border border-input bg-white px-4 py-3 outline-none focus:border-primary focus:ring-2 focus:ring-primary/40" data-testid="quote-phone-input" />
            </div>
          </div>
          <div>
            <label className="mb-2 block text-sm font-semibold">
              Email <span className="text-primary">*</span>
            </label>
            <input type="email" value={form.email} onChange={update("email")} required placeholder="you@example.com" className="w-full rounded-sm border border-input bg-white px-4 py-3 outline-none focus:border-primary focus:ring-2 focus:ring-primary/40" data-testid="quote-email-input" />
          </div>
          <div>
            <label className="mb-2 block text-sm font-semibold">
              Full pickup address <span className="text-primary">*</span>
            </label>
            <input
              type="text"
              value={form.address}
              onChange={update("address")}
              placeholder="Street, city, state (e.g. 1234 Oak Ln, Wesley Chapel, FL)"
              className="w-full rounded-sm border border-input bg-white px-4 py-3 outline-none focus:border-primary focus:ring-2 focus:ring-primary/40"
              data-testid="quote-address-input"
              required
            />
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="text-sm font-semibold">
                Describe the job <span className="text-primary">*</span>
              </label>
              <span className={`text-xs ${descLen >= MIN_DESC ? "text-[hsl(145,27%,34%)]" : "text-muted-foreground"}`}>
                {descLen} / {MIN_DESC} min
              </span>
            </div>
            <textarea
              value={form.description}
              onChange={update("description")}
              rows={4}
              placeholder="Tell us what's being hauled, quantity, size, and any access notes (gate, tight driveway, etc.)."
              className="w-full rounded-sm border border-input bg-white px-4 py-3 outline-none focus:border-primary focus:ring-2 focus:ring-primary/40"
              data-testid="quote-description-input"
              required
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold">
              Photos (optional but recommended)
            </label>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {photos.map((p, i) => (
                <div key={i} className="group relative aspect-square overflow-hidden rounded-sm border border-border">
                  <img src={p.preview} alt="upload preview" className="h-full w-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePhoto(i)}
                    className="absolute right-1 top-1 rounded-full bg-black/70 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"
                    data-testid={`photo-remove-${i}`}
                    aria-label="remove photo"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {photos.length < MAX_PHOTOS && (
                <label className="flex aspect-square cursor-pointer flex-col items-center justify-center gap-1 rounded-sm border-2 border-dashed border-border text-muted-foreground transition-colors hover:border-primary hover:text-primary" data-testid="photo-upload-btn">
                  <Camera className="h-6 w-6" />
                  <span className="text-xs font-semibold">Add photo</span>
                  <input type="file" accept="image/*" multiple onChange={addPhotos} className="hidden" data-testid="photo-input" />
                </label>
              )}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Up to {MAX_PHOTOS} photos · JPG, PNG, HEIC · 8 MB each. Photos get you a faster, firmer quote.
            </p>
          </div>

          <label className="flex cursor-pointer items-start gap-3 rounded-sm border border-border bg-muted/50 p-3">
            <input
              type="checkbox"
              checked={form.consent_contact}
              onChange={(e) => setField("consent_contact")(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-[hsl(16,55%,49%)]"
              data-testid="quote-consent-checkbox"
              required
            />
            <span className="text-xs text-foreground/80">
              <strong>I agree to be contacted</strong> by phone, text, or email about my hauling request.
              Message &amp; data rates may apply. You can opt out anytime by replying STOP. <span className="text-primary">*</span>
            </span>
          </label>

          <div className="flex flex-col gap-3 sm:flex-row">
            <button type="button" onClick={() => setStep(2)} className="rounded-sm border border-border bg-white px-6 py-3 text-sm font-semibold hover:bg-muted" data-testid="quote-back2-btn">
              Back
            </button>
            <button type="submit" disabled={!canSubmit || loading} className="btn-cta flex-1 disabled:cursor-not-allowed disabled:opacity-50" data-testid="quote-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
              Request Pickup
            </button>
          </div>
        </div>
      )}
    </form>
  );
};

export default QuoteForm;
